import React, { useEffect, useState, useMemo } from "react";
import { Calendar, Search } from "lucide-react";
import Swal2 from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import "./app.css";
import Loading from "./loading";
import { secretKey } from "./data";

const MySwal = withReactContent(Swal2);
const API = "https://apiftpsplit.ftpeople.pl";
const hdrs = { "Content-Type": "application/json", Authorization: secretKey };

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(d.getDate()).padStart(2, "0")}`;
};

const fmtPLN = (n) =>
  Number(n || 0).toLocaleString("pl-PL", { minimumFractionDigits: 0 }) + " zł";

const TYPES = ["CHF", "SKD", "WIBOR", "SLUZEBNOSC"];
const TYPE_LABELS = {
  CHF: "CHF",
  SKD: "SKD",
  WIBOR: "WIBOR",
  SLUZEBNOSC: "SŁUŻEBNOŚĆ",
};

const SendClientsResults = () => {
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState([]);
  const [from, setFrom] = useState(todayStr());
  const [to, setTo] = useState(todayStr());

  /* ── fetch ── */
  const getData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/getSendLeadsResults`, {
        method: "POST",
        headers: hdrs,
        body: JSON.stringify({ from, to }),
      });
      const data = await res.json();
      setResults(
        (data || []).sort((a, b) => new Date(b.date) - new Date(a.date))
      );
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    getData();
  }, []);

  const showResults = () => {
    if (!from || !to) return;
    if (new Date(from) > new Date(to)) {
      MySwal.fire({
        icon: "error",
        title: "Błąd dat",
        text: '"Od" nie może być większe niż "Do"',
      });
      return;
    }
    getData();
  };

  /* ── computed ── */
  const dates = useMemo(() => {
    const set = new Set();
    results.forEach((r) => set.add(r.assDate));
    return [...set].sort((a, b) => b.localeCompare(a));
  }, [results]);

  const getSum = (date, type) => {
    return results
      .filter((r) => r.assDate === date && (type === "all" || r.type === type))
      .reduce((s, r) => s + (r.sum || 0), 0);
  };

  const grandTotal = useMemo(
    () => results.reduce((s, r) => s + (r.sum || 0), 0),
    [results]
  );

  const typeTotals = useMemo(() => {
    const m = {};
    TYPES.forEach((t) => {
      m[t] = results
        .filter((r) => r.type === t)
        .reduce((s, r) => s + (r.sum || 0), 0);
    });
    return m;
  }, [results]);

  if (loading) return <Loading />;

  return (
    <div className="scr-wrapper">
      {/* ── Date range ── */}
      <div className="scr-toolbar">
        <div className="db-date-range">
          <div className="db-date-field">
            <span className="db-date-label">od</span>
            <input
              type="date"
              className="db-input"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div className="db-date-field">
            <span className="db-date-label">do</span>
            <input
              type="date"
              className="db-input"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          <button
            className="lq-btn lq-btn--dark db-date-apply"
            onClick={showResults}
          >
            <Search size={14} /> Zastosuj
          </button>
        </div>
      </div>

      {/* ── Summary cards ── */}
      <div className="scr-summary">
        <div className="scr-total">
          <span className="scr-total-label">Suma raportu</span>
          <span className="scr-total-value">{fmtPLN(grandTotal)} netto</span>
        </div>
        <div className="scr-types">
          {TYPES.map((t) => (
            <div key={t} className="scr-type-card">
              <span className="scr-type-label">{TYPE_LABELS[t]}</span>
              <span className="scr-type-value">{fmtPLN(typeTotals[t])}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Table ── */}
      <div className="lq-table-wrap db-table-scroll">
        <table className="lq-table">
          <thead>
            <tr>
              <th>Data</th>
              <th>Suma</th>
              {TYPES.map((t) => (
                <th key={t}>{TYPE_LABELS[t]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dates.map((date) => (
              <tr key={date}>
                <td className="lq-td-mono">{date}</td>
                <td>
                  <strong>{fmtPLN(getSum(date, "all"))}</strong>
                </td>
                {TYPES.map((t) => (
                  <td key={t} className="scr-cell-type">
                    {fmtPLN(getSum(date, t))}
                  </td>
                ))}
              </tr>
            ))}
            {dates.length === 0 && (
              <tr>
                <td colSpan={2 + TYPES.length} className="lq-empty-row">
                  Brak danych dla wybranego zakresu
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SendClientsResults;
